import { Dispatch } from 'redux';
import ShopifyController from '../controllers/ShopifyController';
import commonHandler from '../errorHandlers/common-handler';
import { OauthUrlResponse } from '../model/KlaviyoModel';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';

class ShopifyCRUDActions extends CrudActionContainer {
  retrieveOauthUrlAction: string;
  revokeAction: string;
  retrieveAccounts: string;
  resetStore: string;
  retrieveRevenue: string;

  constructor() {
    super('SHOPIFY');
    this.retrieveOauthUrlAction = this.makeActionKey('RETRIEVED_OAUTH_URL');
    this.retrieveAccounts = this.makeActionKey('RETRIEVED_ACCOUNTS');
    this.revokeAction = this.makeActionKey('REVOKE');
    this.resetStore = this.makeActionKey('RESET_STORE');
    this.retrieveRevenue = this.makeActionKey('RETRIEVED_REVENUE');
  }

  getOauthUrl(shop: string, facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveOauthUrlAction));
      try {
        const response: OauthUrlResponse = await ShopifyController.getOauthUrl(
          shop,
          facebookPageId,
        );
        dispatch({
          type: this.retrieveOauthUrlAction,
        });

        return response.link;
      } catch (error) {
        commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  getAccounts(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAccounts));
      try {
        const response = await ShopifyController.getAccounts(facebookPageId);
        dispatch({
          type: this.retrieveAccounts,
          accounts: response,
        });
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  revoke(tokenId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.revokeAction));

      try {
        await ShopifyController.revokeToken(tokenId);
        dispatch({ type: this.revokeAction, id: tokenId });
        return true;
      } catch (error) {
        commonHandler(dispatch)(error);
        return false;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  getRevenues(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveRevenue));
      try {
        const response = await ShopifyController.getRevenue(facebookPageId);
        dispatch({
          type: this.retrieveRevenue,
          revenue: response,
        });
        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  revokeAccessToken(tokenId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.revokeAction));

      try {
        await ShopifyController.revokeAccessToken(tokenId);
        dispatch({ type: this.revokeAction, id: tokenId });
        return true;
      } catch (error) {
        commonHandler(dispatch)(error);
        return false;
      }
    };
  }

  invalidateStore() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch({ type: this.resetStore });
    };
  }
}

const shopifyCRUDActions = new ShopifyCRUDActions();

export default shopifyCRUDActions;

export const useShopifyCRUDActions = getActionHookBuilder(shopifyCRUDActions, [
  'getOauthUrl',
  'getAccounts',
  'revoke',
  'getRevenues',
  'invalidateStore',
  'revokeAccessToken',
]);
